export const JUDIT_DOCUMENT_TYPES = [
  ['CaseDocument', 'Documento do processo'],
  ['MainDecision', 'Decisão principal / recorrida'],
  ['IntegrativeDecision', 'Decisão integrativa / embargos'],
  ['ClientSubmission', 'Peça do cliente / posição atual confirmada'],
  ['OpponentSubmission', 'Peça da parte contrária'],
]
export const JUDIT_MAX_DOCUMENTS = 8
export const isGuid = (value) => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(value)
export const normalizeCnj = (value = '') => value.replace(/\D/g, '')
export const validCnj = (value) => typeof value === 'string' && /^(?:\d{20}|\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4})$/.test(value.trim())

export function resolveJuditProcess(publication, explicitNumber = '') {
  const explicit = typeof explicitNumber === 'string' ? explicitNumber.trim() : ''
  if (explicit && !validCnj(explicit)) return { numeroProcesso: '', error: 'O número do processo informado não possui formato CNJ válido.' }
  const matches = typeof publication === 'string' ? publication.match(/\b\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}\b|\b\d{20}\b/g) || [] : []
  const numbers = [...new Set(matches.map(normalizeCnj))]
  if (numbers.length > 1 || explicit && numbers.some((number) => number !== normalizeCnj(explicit))) {
    return { numeroProcesso: '', error: 'Há números de processos diferentes no contexto. Confira a publicação antes de buscar documentos.' }
  }
  const numeroProcesso = explicit ? normalizeCnj(explicit) : numbers[0] || ''
  return { numeroProcesso, error: numeroProcesso ? '' : 'O número CNJ do processo ainda não foi identificado no contexto.' }
}

export function acceptJuditProcessList(data, numeroProcesso) {
  if (!validCnj(data?.numeroProcesso) || normalizeCnj(data.numeroProcesso) !== normalizeCnj(numeroProcesso) || !Array.isArray(data.consultas) ||
    data.maisConsultas != null && typeof data.maisConsultas !== 'boolean') {
    throw new Error('A lista local não corresponde ao processo solicitado.')
  }
  return data.consultas.map((consulta) => acceptJuditConsulta(consulta, { numeroProcesso }))
}

export function juditReconcilesUncertain(pending, consulta, { consultas = [], maisConsultas = false } = {}) {
  if (!pending) return true
  if (!consulta || pending.numeroProcesso && normalizeCnj(pending.numeroProcesso) !== normalizeCnj(consulta.numeroProcesso) ||
    pending.instancia && pending.instancia !== consulta.instancia) return false
  if (pending.operacao === 'REOBTER' && (consulta.versao === pending.versaoAnterior || juditPending(consulta) || !consulta.anexos?.some((row) =>
    row.anexoId === pending.anexoId && ['EXTRAIDO', 'ERRO', 'OCR_NECESSARIO', 'LIMITE_EXCEDIDO'].includes(row.estado)))) return false
  if (isGuid(pending.consultaId)) return pending.consultaId === consulta.consultaId
  if (!validCnj(pending.numeroProcesso) || ![1, 2].includes(pending.instancia) || maisConsultas) return false
  const candidates = consultas.filter((item) => normalizeCnj(item.numeroProcesso) === normalizeCnj(pending.numeroProcesso) && item.instancia === pending.instancia)
  return candidates.length === 1 && candidates[0].consultaId === consulta.consultaId
}

export function chooseJuditLocalConsulta(consultas, pending, previous, maisConsultas = false) {
  if (pending) {
    const matching = consultas.filter((consulta) => juditReconcilesUncertain(pending, consulta, { consultas, maisConsultas }))
    return matching.length === 1 ? matching[0] : null
  }
  return consultas.find((item) => item.consultaId === previous?.consultaId) || (!maisConsultas && consultas.length === 1 ? consultas[0] : null)
}

export function juditPending(consulta) {
  return Boolean(consulta && (['CRIADA', 'PENDING', 'ENVIO_INCERTO', 'PROCESSANDO'].includes(consulta.estado) ||
    consulta.anexos?.some((anexo) => ['BAIXANDO', 'EXTRAINDO', 'ENVIO_INCERTO'].includes(anexo.estado))))
}

export function juditSelectable(anexo) {
  return anexo?.estado === 'EXTRAIDO' && isGuid(anexo.documentoId) && isGuid(anexo.documentoVersao) &&
    Number.isInteger(anexo.quantidadePaginas) && anexo.quantidadePaginas > 0 && Boolean(anexo.textoSha256)
}

export function juditSelection(anexo) {
  return { documentoId: anexo.documentoId, documentoVersao: anexo.documentoVersao,
    paginaInicial: 1, paginaFinal: anexo.quantidadePaginas, tipo: 'CaseDocument', posicaoAtualConfirmada: false }
}

export function retainJuditSelections(consulta, selected) {
  return selected.filter((selection) => consulta.anexos?.some((anexo) => juditSelectable(anexo) &&
    anexo.documentoId === selection.documentoId && anexo.documentoVersao === selection.documentoVersao))
}

export function juditSelectionError(consulta, selected, publication = '', processReference = '') {
  if (!consulta) return ''
  if (!isGuid(consulta.consultaId) || !isGuid(consulta.versao) || !validCnj(consulta.numeroProcesso)) return 'A consulta não possui identificação ou versão válida. Releia a consulta salva.'
  if (processReference && normalizeCnj(processReference) !== normalizeCnj(consulta.numeroProcesso)) return 'O processo atual difere da consulta salva. Confira os documentos deste processo antes de gerar.'
  if (juditPending(consulta)) return 'Aguarde e consulte o andamento da busca antes de gerar com seus documentos.'
  if (consulta.estado !== 'PRONTA') return 'A consulta não está pronta para usar seus documentos. Releia seu estado e os avisos.'
  if (!selected.length) return 'Confira e selecione ao menos um documento textual para usar esta consulta na minuta.'
  if (selected.length > JUDIT_MAX_DOCUMENTS || new Set(selected.map((item) => item.documentoId)).size !== selected.length) return 'Selecione até oito documentos distintos.'
  const knownCnjs = publication.match(/\b\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}\b|\b\d{20}\b/g) || []
  if (!knownCnjs.length) return 'Inclua o número CNJ deste processo no texto da publicação / contexto para confirmar a vinculação dos documentos.'
  if (knownCnjs.some((cnj) => normalizeCnj(cnj) !== normalizeCnj(consulta.numeroProcesso))) return 'O processo da publicação difere da consulta. Confira o CNJ antes de gerar.'
  for (const selection of selected) {
    const anexo = consulta.anexos?.find((item) => item.documentoId === selection.documentoId)
    if (!juditSelectable(anexo) || anexo.documentoVersao !== selection.documentoVersao) return 'Um documento selecionado mudou ou não possui texto extraído. Confira novamente.'
    if (!Number.isInteger(selection.paginaInicial) || !Number.isInteger(selection.paginaFinal) || selection.paginaInicial < 1 ||
      selection.paginaFinal < selection.paginaInicial || selection.paginaFinal > anexo.quantidadePaginas) return 'Confira os intervalos de páginas dos documentos selecionados.'
    if (!JUDIT_DOCUMENT_TYPES.some(([type]) => type === selection.tipo)) return 'Informe a função de cada documento.'
    if (selection.tipo === 'ClientSubmission' && selection.posicaoAtualConfirmada !== true) return 'Confirme expressamente se a peça do cliente representa sua posição atual.'
  }
  return ''
}

export function juditGenerationContext(consulta, selected) {
  if (!consulta) return {}
  return { consultaJuditId: consulta.consultaId, consultaJuditVersao: consulta.versao,
    processReference: normalizeCnj(consulta.numeroProcesso), documentosJudit: selected.map((selection) => ({
      documentoId: selection.documentoId, documentoVersao: selection.documentoVersao,
      paginaInicial: selection.paginaInicial, paginaFinal: selection.paginaFinal,
      tipo: selection.tipo, posicaoAtualConfirmada: selection.posicaoAtualConfirmada === true,
    })) }
}

export function acceptJuditConsulta(data, expected = {}) {
  if (!isGuid(data?.consultaId) || !isGuid(data?.versao) || !Array.isArray(data?.anexos) ||
    !validCnj(data.numeroProcesso) || ![1, 2].includes(data.instancia) ||
    (expected.consultaId && data.consultaId !== expected.consultaId) ||
    (expected.numeroProcesso && normalizeCnj(data.numeroProcesso) !== normalizeCnj(expected.numeroProcesso)) ||
    (expected.instancia && data.instancia !== Number(expected.instancia))) throw new Error('A resposta não corresponde à consulta solicitada. Releia a consulta pelo ID antes de continuar.')
  return data
}

export function juditRefreshWait(consulta, now = Date.now()) {
  const until = Date.parse(consulta?.proximaAtualizacaoEm || '')
  return Number.isFinite(until) ? Math.max(0, Math.ceil((until - now) / 1000)) : 0
}

export function juditSourceAge(value, now = Date.now()) {
  const timestamp = Date.parse(value || '')
  if (!Number.isFinite(timestamp)) return 'idade da fonte não informada'
  const minutes = Math.max(0, Math.floor((now - timestamp) / 60000))
  if (minutes < 60) return `fonte atualizada há ${minutes} minuto(s)`
  const hours = Math.floor(minutes / 60)
  return hours < 48 ? `fonte atualizada há ${hours} hora(s)` : `fonte atualizada há ${Math.floor(hours / 24)} dia(s)`
}

export function juditUncertain(response) {
  return !response?.status || response.status >= 500 || response.code === 'JUDIT_ENVIO_INCERTO'
}

export function juditError(response) {
  if (response?.code === 'JUDIT_INSTANCE_REQUIRED') return 'Confirme o grau do processo para solicitar os documentos. Nenhuma nova busca foi criada.'
  if (response?.code === 'JUDIT_INSTANCE_CONFLICT') return 'O grau informado diverge do link do processo na publicação. Confira o grau antes de solicitar os documentos.'
  if (response?.code === 'JUDIT_INVALID_INPUT' || response?.code === 'JUDIT_INPUT_INVALID') return 'Confira o número CNJ da publicação, inclusive o dígito verificador e o grau confirmado.'
  if (response?.status === 429) return 'Aguarde o intervalo da próxima atualização. Releia a consulta salva antes de tentar atualizar novamente.'
  if (response?.status === 409) return 'A versão ou o estado da consulta mudou. Releia a consulta salva antes de continuar.'
  if (response?.status === 401 || response?.status === 403) return 'Sua sessão ou permissão não permite esta operação. Nenhuma chamada será repetida automaticamente.'
  if (response?.status === 404) return 'Consulta ou documento não encontrado para seu acesso. Releia a lista local.'
  if (response?.status === 413) return 'O documento excede o limite de extração. Ele permanece no índice e não pode ser usado como texto.'
  return 'Não foi possível confirmar esta operação. Releia os documentos locais deste processo antes de continuar.'
}
